import { Container, Graphics, Sprite, Texture } from 'pixi.js';
import {
  loadSpriteSheetWithTransparency,
  loadTextureWithTransparency,
} from '../assets/spriteProcessor';

export type CrateType = 'weapon' | 'health' | 'utility';

export interface ProjectileSpriteResult {
  sprite: Sprite | Graphics;
  scale: number;
}

export interface KamikazeSpriteResult {
  container: Container;
  hasTextures: boolean;
  textures?: Texture[];
}

export class SpriteManager {
  private _wormTextures: Texture[] = [];
  private _grenadeTextures: Texture[] = [];
  private _missileTextures: Texture[] = [];
  private _clusterTextures: Texture[] = [];
  private _dynamiteTextures: Texture[] = [];
  private _mineTextures: Texture[] = [];
  private _mineOnTextures: Texture[] = [];
  private _homingTextures: Texture[] = [];
  private _crosshairTextures: Texture[] = [];
  private _barrelTextures: Texture[] = [];
  private _weaponCrateTextures: Texture[] = [];
  private _healthCrateTextures: Texture[] = [];
  private _weaponCrateParaTextures: Texture[] = [];
  private _healthCrateParaTextures: Texture[] = [];
  private _utilityCrateParaTextures: Texture[] = [];
  private _utilityCrateTextures: Texture[] = [];
  private _graveTextures: Texture[] = [];
  private _sheepTextures: Texture[] = [];
  private _holyGrenadeTextures: Texture[] = [];
  private _bananaTextures: Texture[] = [];
  private _mortarTextures: Texture[] = [];
  private _molotovTextures: Texture[] = [];
  private _kamikazeTextures: Texture[][] = [];
  private _superSheepTextures: Texture[] = [];
  private _weaponIconTextures = new Map<string, Texture>();
  private _isLoaded = false;

  get wormTextures(): Texture[] {
    return this._wormTextures;
  }
  get grenadeTextures(): Texture[] {
    return this._grenadeTextures;
  }
  get missileTextures(): Texture[] {
    return this._missileTextures;
  }
  get clusterTextures(): Texture[] {
    return this._clusterTextures;
  }
  get dynamiteTextures(): Texture[] {
    return this._dynamiteTextures;
  }
  get mineTextures(): Texture[] {
    return this._mineTextures;
  }
  get mineOnTextures(): Texture[] {
    return this._mineOnTextures;
  }
  get homingTextures(): Texture[] {
    return this._homingTextures;
  }
  get crosshairTextures(): Texture[] {
    return this._crosshairTextures;
  }
  get barrelTextures(): Texture[] {
    return this._barrelTextures;
  }
  get weaponCrateTextures(): Texture[] {
    return this._weaponCrateTextures;
  }
  get healthCrateTextures(): Texture[] {
    return this._healthCrateTextures;
  }
  get weaponCrateParaTextures(): Texture[] {
    return this._weaponCrateParaTextures;
  }
  get healthCrateParaTextures(): Texture[] {
    return this._healthCrateParaTextures;
  }
  get utilityCrateParaTextures(): Texture[] {
    return this._utilityCrateParaTextures;
  }
  get utilityCrateTextures(): Texture[] {
    return this._utilityCrateTextures;
  }
  get graveTextures(): Texture[] {
    return this._graveTextures;
  }
  get sheepTextures(): Texture[] {
    return this._sheepTextures;
  }
  get holyGrenadeTextures(): Texture[] {
    return this._holyGrenadeTextures;
  }
  get bananaTextures(): Texture[] {
    return this._bananaTextures;
  }
  get mortarTextures(): Texture[] {
    return this._mortarTextures;
  }
  get molotovTextures(): Texture[] {
    return this._molotovTextures;
  }
  get kamikazeTextures(): Texture[][] {
    return this._kamikazeTextures;
  }
  get superSheepTextures(): Texture[] {
    return this._superSheepTextures;
  }
  get weaponIconTextures(): Map<string, Texture> {
    return this._weaponIconTextures;
  }
  get isLoaded(): boolean {
    return this._isLoaded;
  }

  async loadSprites(): Promise<void> {
    try {
      this._wormTextures = await loadSpriteSheetWithTransparency('/sprites/Worms/wbrth1.png', 60);
      this._grenadeTextures = await loadSpriteSheetWithTransparency(
        '/sprites/Weapons/grenade.png',
        60,
      );
      this._missileTextures = await loadSpriteSheetWithTransparency(
        '/sprites/Weapons/missile.png',
        60,
      );
      this._clusterTextures = await loadSpriteSheetWithTransparency(
        '/sprites/Weapons/cluster.png',
        60,
      );
      this._dynamiteTextures = await loadSpriteSheetWithTransparency(
        '/sprites/Weapons/dynamite.png',
        60,
      );
      this._mineTextures = await loadSpriteSheetWithTransparency(
        '/sprites/Weapons/mineoff.png',
        60,
      );
      this._mineOnTextures = await loadSpriteSheetWithTransparency(
        '/sprites/Weapons/mineon.png',
        60,
      );
      this._homingTextures = await loadSpriteSheetWithTransparency(
        '/sprites/Weapons/hmissil1.png',
        60,
      );
      this._crosshairTextures = await loadSpriteSheetWithTransparency(
        '/sprites/Misc/crshairr.png',
        60,
      );
      this._barrelTextures = await loadSpriteSheetWithTransparency(
        '/sprites/Misc/oildrum1.png',
        60,
      );
      this._weaponCrateTextures = await loadSpriteSheetWithTransparency(
        '/sprites/Misc/wcrate1.png',
        60,
      );
      this._healthCrateTextures = await loadSpriteSheetWithTransparency(
        '/sprites/Misc/mcrate1.png',
        60,
      );
      this._weaponCrateParaTextures = await loadSpriteSheetWithTransparency(
        '/sprites/Misc/wcratev.png',
        60,
      );
      this._healthCrateParaTextures = await loadSpriteSheetWithTransparency(
        '/sprites/Misc/mcratev.png',
        60,
      );
      this._utilityCrateParaTextures = await loadSpriteSheetWithTransparency(
        '/sprites/Misc/ucratev.png',
        60,
      );
      this._utilityCrateTextures = await loadSpriteSheetWithTransparency(
        '/sprites/Misc/ucrate1.png',
        60,
      );
      this._graveTextures = await loadSpriteSheetWithTransparency('/sprites/Misc/grave1.png', 60);
      this._sheepTextures = await loadSpriteSheetWithTransparency(
        '/sprites/Weapons/sheepwlk.png',
        60,
      );

      const spsheep1Frames = await loadSpriteSheetWithTransparency(
        '/sprites/Weapons/spsheep1.png',
        60,
      );
      const spsheep2Frames = await loadSpriteSheetWithTransparency(
        '/sprites/Weapons/spsheep2.png',
        60,
      );
      this._superSheepTextures = [spsheep1Frames[8], spsheep2Frames[8]];

      this._holyGrenadeTextures = await loadSpriteSheetWithTransparency(
        '/sprites/Weapons/hgrenade.png',
        60,
      );
      this._bananaTextures = await loadSpriteSheetWithTransparency(
        '/sprites/Weapons/banana.png',
        60,
      );
      this._mortarTextures = await loadSpriteSheetWithTransparency(
        '/sprites/Weapons/mortar.png',
        60,
      );
      this._molotovTextures = await loadSpriteSheetWithTransparency(
        '/sprites/Weapons/petrolbm.png',
        60,
      );

      try {
        for (let i = 1; i <= 5; i++) {
          const frames = await loadSpriteSheetWithTransparency(`/sprites/Worms/wkami${i}.png`, 60);
          this._kamikazeTextures.push(frames);
        }
      } catch (e) {
        console.warn('Failed to load kamikaze sprites:', e);
      }

      await this.loadWeaponIcons();

      this._isLoaded = true;

      console.log('Loaded sprites:', {
        worm: this._wormTextures.length,
        grenade: this._grenadeTextures.length,
        missile: this._missileTextures.length,
        cluster: this._clusterTextures.length,
        dynamite: this._dynamiteTextures.length,
        mine: this._mineTextures.length,
        crosshair: this._crosshairTextures.length,
        barrel: this._barrelTextures.length,
        weaponCrate: this._weaponCrateTextures.length,
        healthCrate: this._healthCrateTextures.length,
        utilityCrate: this._utilityCrateTextures.length,
        grave: this._graveTextures.length,
        weaponIcons: this._weaponIconTextures.size,
      });
    } catch (e) {
      console.warn('Failed to load sprites, using placeholders:', e);
    }
  }

  async loadWeaponIcons(): Promise<void> {
    const iconMap: Record<string, string> = {
      grenade: 'grenade',
      bazooka: 'bazooka',
      dynamite: 'dynamite',
      cluster: 'cluster',
      shotgun: 'shotgun',
      rope: 'rope',
      airstrike: 'airstrke',
      homing: 'hmissile',
      mine: 'mine',
      teleport: 'teleport',
      parachute: 'parachut',
      firePunch: 'firepnch',
      baseballBat: 'baseball',
      blowtorch: 'blwtorch',
      drill: 'drill',
      holyHandGrenade: 'hgrenade',
      sheep: 'sheep',
      banana: 'banana',
      mortar: 'mortar',
      molotov: 'petrolbm',
      napalmStrike: 'firestrk',
      kamikaze: 'kamikaze',
      bungee: 'bungee',
      jetpack: 'jetpack',
      girder: 'girder',
      supersheep: 'sprsheep',
    };

    for (const [weaponId, iconName] of Object.entries(iconMap)) {
      try {
        const texture = await loadTextureWithTransparency(
          `/sprites/Weapon Icons/${iconName}.1.png`,
        );
        this._weaponIconTextures.set(weaponId, texture);
      } catch (e) {
        console.warn(`Failed to load icon for ${weaponId}`);
      }
    }
  }

  getWeaponIcon(weaponId: string): Texture | undefined {
    return this._weaponIconTextures.get(weaponId);
  }

  createProjectileSprite(type: string): ProjectileSpriteResult {
    let texture: Texture | undefined;
    let scale = 1;

    // Grub balls are round bodies (not the grenade sprite): a glowing blue
    // sphere with a highlight, drawn directly so no asset is needed.
    if (type === 'grub') {
      const ball = new Graphics();
      ball.circle(0, 0, 7);
      ball.fill({ color: 0x2a6cff });
      ball.stroke({ width: 1.5, color: 0x9fc4ff });
      ball.circle(-2.2, -2.2, 2);
      ball.fill({ color: 0xdcebff, alpha: 0.85 });
      return { sprite: ball, scale: 1 };
    }

    // Aduka tracers: small purple energy spheres.
    if (type === 'aduka') {
      const ball = new Graphics();
      ball.circle(0, 0, 6);
      ball.fill({ color: 0x9b3cff });
      ball.stroke({ width: 1.5, color: 0xd8a6ff });
      ball.circle(-1.8, -1.8, 1.8);
      ball.fill({ color: 0xf0e0ff, alpha: 0.85 });
      return { sprite: ball, scale: 1 };
    }

    // Grub SS: a bigger, hotter electric ball (yellow-white core).
    if (type === 'grubSS') {
      const ball = new Graphics();
      ball.circle(0, 0, 9);
      ball.fill({ color: 0xffe14d });
      ball.stroke({ width: 1.8, color: 0xfff7c0 });
      ball.circle(0, 0, 5);
      ball.fill({ color: 0xfff7d0, alpha: 0.9 });
      ball.circle(-2.6, -2.6, 2.4);
      ball.fill({ color: 0xffffff, alpha: 0.95 });
      return { sprite: ball, scale: 1 };
    }

    // Nak: a stubby dark artillery shell with a brass nose.
    if (type === 'nak') {
      const shell = new Graphics();
      shell.roundRect(-4, -8, 8, 16, 3);
      shell.fill({ color: 0x3a4150 });
      shell.stroke({ width: 1.5, color: 0x10140a });
      shell.ellipse(0, -7, 4, 3).fill({ color: 0xc8a23a });
      shell.rect(-4, 4, 8, 3).fill({ color: 0x20242e });
      return { sprite: shell, scale: 1 };
    }

    // Kalsiddon: a dark bomb with a red band (splits into homing missiles).
    if (type === 'kalsiddon') {
      const bomb = new Graphics();
      bomb.circle(0, 0, 8).fill({ color: 0x2e3650 }).stroke({ width: 1.5, color: 0x10140a });
      bomb.rect(-8, -1.5, 16, 3).fill({ color: 0xe0483a }); // red band
      bomb.circle(-2.4, -2.4, 2.2).fill({ color: 0x8fa0c8, alpha: 0.85 });
      return { sprite: bomb, scale: 1 };
    }

    // J.D. Magnet Sphere (T2): a blue electric orb ringed by an attraction
    // halo (it pulls worms inward).
    if (type === 'jdT2') {
      const ball = new Graphics();
      ball.circle(0, 0, 9).stroke({ width: 1.2, color: 0x6fd2ff, alpha: 0.55 });
      ball.circle(0, 0, 6.5).fill({ color: 0x2a6cff }).stroke({ width: 1.5, color: 0xbfe0ff });
      ball.circle(-2, -2, 2).fill({ color: 0xeaffff, alpha: 0.9 });
      return { sprite: ball, scale: 1 };
    }

    // J.D. Anti-Magnet (SS): a bigger orange-white orb with outward spikes
    // (it shoves worms away).
    if (type === 'jdSS') {
      const ball = new Graphics();
      for (let a = 0; a < 8; a++) {
        const ang = (a / 8) * Math.PI * 2;
        ball
          .moveTo(Math.cos(ang) * 7, Math.sin(ang) * 7)
          .lineTo(Math.cos(ang) * 12, Math.sin(ang) * 12)
          .stroke({ width: 1.6, color: 0xffb24d, alpha: 0.85 });
      }
      ball.circle(0, 0, 8).fill({ color: 0xff7a2a }).stroke({ width: 1.8, color: 0xffe0b0 });
      ball.circle(-2.4, -2.4, 2.4).fill({ color: 0xfff2dc, alpha: 0.95 });
      return { sprite: ball, scale: 1 };
    }

    // Trico: a leafy green lettuce ball.
    if (type === 'trico') {
      const ball = new Graphics();
      ball.circle(0, 0, 7);
      ball.fill({ color: 0x57c23f });
      ball.stroke({ width: 1.5, color: 0x2f7a22 });
      // leaf veins
      ball.moveTo(0, -6).lineTo(0, 6).stroke({ width: 1, color: 0x2f7a22, alpha: 0.7 });
      ball.moveTo(-6, 0).lineTo(6, 0).stroke({ width: 1, color: 0x2f7a22, alpha: 0.7 });
      ball.circle(-2.2, -2.2, 2).fill({ color: 0x9be07f, alpha: 0.85 });
      return { sprite: ball, scale: 1 };
    }

    // Knight: a small steel tracer orb (no damage; the swords are the payload).
    if (type === 'knight') {
      const ball = new Graphics();
      ball.circle(0, 0, 5);
      ball.fill({ color: 0xb8c4d8 });
      ball.stroke({ width: 1.5, color: 0xeef4ff });
      ball.circle(-1.4, -1.4, 1.5);
      ball.fill({ color: 0xffffff, alpha: 0.9 });
      return { sprite: ball, scale: 1 };
    }

    // Lightning T1/T2: a crackling pale-blue electric orb.
    if (type === 'lightningT1' || type === 'lightningT2') {
      const ball = new Graphics();
      ball.circle(0, 0, 7);
      ball.fill({ color: 0x66e0ff });
      ball.stroke({ width: 1.5, color: 0xeaffff });
      ball.circle(-2.2, -2.2, 2.2);
      ball.fill({ color: 0xffffff, alpha: 0.9 });
      return { sprite: ball, scale: 1 };
    }

    // Aduka SS: a longer charged phase shell with a glowing cyan aura + nose.
    if (type === 'adukaSS') {
      const shell = new Graphics();
      shell.ellipse(0, 0, 8, 13).fill({ color: 0x29e0e0, alpha: 0.25 }); // aura
      shell.roundRect(-4, -9, 8, 18, 3);
      shell.fill({ color: 0x243a4a });
      shell.stroke({ width: 1.5, color: 0x10140a });
      shell.ellipse(0, -8, 4, 3).fill({ color: 0x66f2f2 }); // glowing nose
      shell.rect(-4, 5, 8, 3).fill({ color: 0x18222a });
      return { sprite: shell, scale: 1 };
    }

    if (type === 'bazooka' && this._missileTextures.length > 0) {
      texture = this._missileTextures[0];
    } else if (type === 'cluster' && this._clusterTextures.length > 0) {
      texture = this._clusterTextures[0];
    } else if (type === 'dynamite' && this._dynamiteTextures.length > 0) {
      texture = this._dynamiteTextures[0];
    } else if (type === 'clusterlet' && this._clusterTextures.length > 0) {
      texture = this._clusterTextures[0];
      scale = 0.5;
    } else if (type === 'holyGrenade' && this._holyGrenadeTextures.length > 0) {
      texture = this._holyGrenadeTextures[0];
    } else if ((type === 'banana' || type === 'bananalet') && this._bananaTextures.length > 0) {
      texture = this._bananaTextures[0];
      scale = type === 'bananalet' ? 0.6 : 1;
    } else if (type === 'mortar' && this._mortarTextures.length > 0) {
      texture = this._mortarTextures[0];
    } else if (type === 'molotov' && this._molotovTextures.length > 0) {
      texture = this._molotovTextures[0];
    } else if (this._grenadeTextures.length > 0) {
      texture = this._grenadeTextures[0];
    }

    if (texture) {
      const sprite = new Sprite(texture);
      sprite.anchor.set(0.5, 0.5);
      sprite.scale.set(scale);
      return { sprite, scale };
    }

    const fallbackSprite = new Graphics();
    fallbackSprite.circle(0, 0, 6);
    fallbackSprite.fill({ color: 0x228b22 });

    let fallbackScale = 2;
    if (type === 'clusterlet') {
      fallbackScale = 1;
    } else if (type === 'bananalet') {
      fallbackScale = 1.2;
    }

    return { sprite: fallbackSprite, scale: fallbackScale };
  }

  createMineSprite(): Sprite | Graphics {
    if (this._mineTextures.length > 0) {
      const sprite = new Sprite(this._mineTextures[0]);
      sprite.anchor.set(0.5, 0.5);
      return sprite;
    }

    const graphics = new Graphics();
    graphics.circle(0, 0, 8);
    graphics.fill({ color: 0x333333 });
    graphics.circle(0, -3, 3);
    graphics.fill({ color: 0xff0000 });
    return graphics;
  }

  createHomingMissileSprite(): Container {
    const container = new Container();

    let sprite: Sprite | Graphics;
    if (this._homingTextures.length > 0) {
      sprite = new Sprite(this._homingTextures[0]);
      (sprite as Sprite).anchor.set(0.5, 0.5);
    } else if (this._missileTextures.length > 0) {
      sprite = new Sprite(this._missileTextures[0]);
      (sprite as Sprite).anchor.set(0.5, 0.5);
    } else {
      const g = new Graphics();
      g.moveTo(0, -10);
      g.lineTo(-4, 6);
      g.lineTo(4, 6);
      g.closePath();
      g.fill({ color: 0xff6600 });
      sprite = g;
    }
    container.addChild(sprite);

    const trail = new Graphics();
    trail.label = 'trail';
    container.addChild(trail);

    return container;
  }

  createSheepSprite(kind: 'sheep' | 'frog' = 'sheep'): Sprite | Graphics {
    // Frog: a little green blob (no sheep texture), so it reads distinctly
    // from the white hopping sheep.
    if (kind === 'frog') {
      const g = new Graphics();
      g.ellipse(0, 1, 9, 6);
      g.fill({ color: 0x3aa34a });
      g.stroke({ width: 1, color: 0x1f6b2c });
      // Eyes.
      g.circle(-3.5, -3, 2.2);
      g.circle(3.5, -3, 2.2);
      g.fill({ color: 0xffffff });
      g.circle(-3.5, -3, 1);
      g.circle(3.5, -3, 1);
      g.fill({ color: 0x000000 });
      return g;
    }

    if (this._sheepTextures.length > 0) {
      const sprite = new Sprite(this._sheepTextures[0]);
      sprite.anchor.set(0.5, 0.5);
      return sprite;
    }

    const g = new Graphics();
    g.ellipse(0, 0, 8, 6);
    g.fill({ color: 0xffffff });
    g.circle(-4, -4, 3);
    g.fill({ color: 0x000000 });
    return g;
  }

  createSuperSheepSprite(): Container {
    const container = new Container();

    if (this._superSheepTextures.length > 0) {
      const sprite = new Sprite(this._superSheepTextures[0]);
      sprite.anchor.set(0.5, 0.5);
      sprite.label = 'spsheep';
      container.addChild(sprite);
    } else {
      const g = new Graphics();
      g.ellipse(0, 0, 10, 7);
      g.fill({ color: 0xffffff });
      g.circle(-5, -5, 4);
      g.fill({ color: 0xff0000 });
      container.addChild(g);
    }

    return container;
  }

  createKamikazeSprite(angleIndex: number): KamikazeSpriteResult {
    const container = new Container();
    const textures = this._kamikazeTextures[angleIndex];

    if (textures && textures.length > 0) {
      const sprite = new Sprite(textures[0]);
      sprite.anchor.set(0.5, 0.5);
      sprite.label = 'kami';
      container.addChild(sprite);
      return { container, hasTextures: true, textures };
    }

    const fallback = new Graphics();
    fallback.circle(0, 0, 10);
    fallback.fill({ color: 0xff0000 });
    container.addChild(fallback);
    return { container, hasTextures: false };
  }

  getKamikazeAngleIndex(dirX: number, dirY: number): number {
    const angle = Math.atan2(-dirY, Math.abs(dirX)) * (180 / Math.PI);
    if (angle >= 60) return 0;
    if (angle >= 30) return 1;
    if (angle >= -30) return 2;
    if (angle >= -60) return 3;
    return 4;
  }

  createNapalmDropletSprite(): Graphics {
    const sprite = new Graphics();
    sprite.ellipse(0, 0, 3, 6);
    sprite.fill({ color: 0xff6600, alpha: 0.9 });
    sprite.ellipse(0, -2, 2, 3);
    sprite.fill({ color: 0xffcc00, alpha: 0.8 });
    return sprite;
  }

  createCrateSprite(type: CrateType, withParachute: boolean): Container {
    const container = new Container();

    let crateTextures: Texture[] = [];
    let crateParaTextures: Texture[] = [];

    if (type === 'weapon') {
      crateTextures = this._weaponCrateTextures;
      crateParaTextures = this._weaponCrateParaTextures;
    } else if (type === 'health') {
      crateTextures = this._healthCrateTextures;
      crateParaTextures = this._healthCrateParaTextures;
    } else {
      crateTextures = this._utilityCrateTextures;
      crateParaTextures = this._utilityCrateParaTextures;
    }

    if (crateTextures.length > 0) {
      const sprite = new Sprite(crateTextures[0]);
      sprite.anchor.set(0.5, 0.5);
      sprite.label = 'box';
      container.addChild(sprite);
    } else {
      const box = new Graphics();
      const boxColor = type === 'health' ? 0xff0000 : type === 'weapon' ? 0x0066ff : 0x00ff00;
      box.rect(-8, -8, 16, 16);
      box.fill({ color: boxColor });
      box.stroke({ width: 1, color: 0x000000 });

      if (type === 'health') {
        box.rect(-5, -2, 10, 4);
        box.fill({ color: 0xffffff });
        box.rect(-2, -5, 4, 10);
        box.fill({ color: 0xffffff });
      } else if (type === 'weapon') {
        box.moveTo(-4, 0);
        box.lineTo(4, 0);
        box.stroke({ width: 2, color: 0xffffff });
        box.moveTo(2, -3);
        box.lineTo(4, 0);
        box.lineTo(2, 3);
        box.stroke({ width: 2, color: 0xffffff });
      }
      box.label = 'box';
      container.addChild(box);
    }

    if (withParachute) {
      if (crateParaTextures.length > 0) {
        const paraSprite = new Sprite(crateParaTextures[0]);
        paraSprite.anchor.set(0.5, 0.5);
        paraSprite.label = 'parachute';
        container.addChild(paraSprite);
      } else {
        const parachute = new Graphics();
        parachute.moveTo(-12, -8);
        parachute.bezierCurveTo(-12, -25, 0, -30, 0, -30);
        parachute.bezierCurveTo(0, -30, 12, -25, 12, -8);
        parachute.lineTo(-12, -8);
        parachute.fill({ color: 0xffcc00, alpha: 0.9 });
        parachute.stroke({ width: 1, color: 0x000000 });
        parachute.moveTo(-10, -10);
        parachute.lineTo(0, -8);
        parachute.stroke({ width: 1, color: 0x8b4513 });
        parachute.moveTo(0, -30);
        parachute.lineTo(0, -8);
        parachute.stroke({ width: 1, color: 0x8b4513 });
        parachute.moveTo(10, -10);
        parachute.lineTo(0, -8);
        parachute.stroke({ width: 1, color: 0x8b4513 });
        parachute.label = 'parachute';
        container.addChild(parachute);
      }
    }

    return container;
  }

  createBarrelSprite(): Container {
    if (this._barrelTextures.length > 0) {
      const sprite = new Sprite(this._barrelTextures[0]);
      sprite.anchor.set(0.5, 1);
      return sprite;
    }

    const container = new Container();
    const g = new Graphics();
    g.roundRect(-8, -18, 16, 22, 3);
    g.fill({ color: 0x8b4513 });
    g.stroke({ width: 1, color: 0x5d3a1a });
    g.rect(-6, -16, 12, 2);
    g.fill({ color: 0x654321 });
    g.rect(-6, -6, 12, 2);
    g.fill({ color: 0x654321 });
    g.rect(-6, 2, 12, 2);
    g.fill({ color: 0x654321 });
    g.circle(0, -8, 3);
    g.fill({ color: 0xff6600 });
    g.stroke({ width: 1, color: 0xff0000 });
    container.addChild(g);
    return container;
  }

  createFireSprite(): Graphics {
    return new Graphics();
  }

  createGraveSprite(): Container {
    if (this._graveTextures.length > 0) {
      const sprite = new Sprite(this._graveTextures[0]);
      sprite.anchor.set(0.5, 1);
      return sprite;
    }

    const container = new Container();
    const g = new Graphics();
    g.roundRect(-6, -16, 12, 18, 2);
    g.fill({ color: 0x555555 });
    g.stroke({ width: 1, color: 0x333333 });
    g.rect(-4, -20, 8, 6);
    g.fill({ color: 0x555555 });
    g.stroke({ width: 1, color: 0x333333 });
    container.addChild(g);
    return container;
  }

  updateProjectileSprite(
    sprite: Sprite | Graphics,
    projectile: { position: { x: number; y: number }; getRotation(): number },
  ): void {
    sprite.position.set(projectile.position.x, projectile.position.y);
    sprite.rotation = projectile.getRotation();
  }

  updateMineSprite(
    sprite: Sprite | Graphics,
    mine: { position: { x: number; y: number }; armed: boolean },
  ): void {
    sprite.position.set(mine.position.x, mine.position.y);
    if (mine.armed && this._mineOnTextures.length > 0 && sprite instanceof Sprite) {
      sprite.texture = this._mineOnTextures[0];
    } else if (mine.armed) {
      (sprite as any).tint = 0xff6666;
    }
  }

  updateHomingMissileSprite(
    container: Container,
    homing: {
      position: { x: number; y: number };
      getRotation(): number;
      trailPositions: Array<{ x: number; y: number }>;
    },
  ): void {
    container.position.set(homing.position.x, homing.position.y);
    const missileSprite = container.children[0] as Sprite;
    if (missileSprite) {
      missileSprite.rotation = homing.getRotation();
    }

    const trail = container.getChildByLabel('trail') as Graphics;
    if (trail && homing.trailPositions.length > 1) {
      trail.clear();
      trail.moveTo(
        homing.trailPositions[0].x - homing.position.x,
        homing.trailPositions[0].y - homing.position.y,
      );
      for (let i = 1; i < homing.trailPositions.length; i++) {
        const alpha = i / homing.trailPositions.length;
        trail.lineTo(
          homing.trailPositions[i].x - homing.position.x,
          homing.trailPositions[i].y - homing.position.y,
        );
        trail.stroke({ width: 2, color: 0xff6600, alpha: alpha * 0.5 });
        trail.moveTo(
          homing.trailPositions[i].x - homing.position.x,
          homing.trailPositions[i].y - homing.position.y,
        );
      }
    }
  }

  updateSheepSprite(
    sprite: Sprite | Graphics,
    sheep: { position: { x: number; y: number }; facing: 'left' | 'right' },
  ): void {
    sprite.position.set(sheep.position.x + 8, sheep.position.y + 6);
    sprite.scale.x = sheep.facing === 'right' ? 1 : -1;
  }

  /** MiniRaon penguin bot: black body, white belly, orange beak, status light. */
  createRaonSprite(): Graphics {
    const g = new Graphics();
    g.ellipse(0, 0, 8, 10);
    g.fill({ color: 0x1c2230 });
    g.ellipse(0, 2, 4.5, 6.5);
    g.fill({ color: 0xf2f2f2 });
    // eyes + beak
    g.circle(-2.5, -4, 1.4);
    g.circle(2.5, -4, 1.4);
    g.fill({ color: 0xffffff });
    g.circle(-2.5, -4, 0.7);
    g.circle(2.5, -4, 0.7);
    g.fill({ color: 0x000000 });
    g.poly([-1.5, -2, 1.5, -2, 0, 1]);
    g.fill({ color: 0xffa024 });
    // antenna stalk; the light is a separate child so it can recolour by state
    g.moveTo(0, -10);
    g.lineTo(0, -14);
    g.stroke({ width: 1, color: 0x888888 });
    const light = new Graphics();
    light.circle(0, -15, 2);
    light.fill({ color: 0xff3030 });
    light.label = 'antennaLight';
    g.addChild(light);
    return g;
  }

  updateRaonSprite(
    sprite: Sprite | Graphics,
    raon: { position: { x: number; y: number }; facing: 'left' | 'right'; armed?: boolean },
  ): void {
    sprite.position.set(raon.position.x + 8, raon.position.y + 7);
    sprite.scale.x = raon.facing === 'right' ? 1 : -1;
    // Antenna light: green when locked on an enemy (armed), red when idle.
    const light = sprite.getChildByLabel?.('antennaLight') as Graphics | null;
    if (light) {
      light.clear();
      light.circle(0, -15, 2);
      light.fill({ color: raon.armed ? 0x35e05a : 0xff3030 });
    }
  }

  updateSuperSheepSprite(
    container: Container,
    superSheep: {
      position: { x: number; y: number };
      facing: 'left' | 'right';
      isFlying: boolean;
      flyingAngle: number;
    },
  ): void {
    container.position.set(superSheep.position.x + 8, superSheep.position.y + 6);
    container.scale.x = superSheep.facing === 'right' ? 1 : -1;
    container.scale.y = 1;

    if (superSheep.isFlying) {
      const displayAngle =
        superSheep.facing === 'right' ? superSheep.flyingAngle : -superSheep.flyingAngle;
      container.rotation = (displayAngle * Math.PI) / 180;
    } else {
      container.rotation = 0;
    }

    if (this._superSheepTextures.length > 1) {
      const sprite = container.getChildByLabel('spsheep') as Sprite;
      if (sprite) {
        const frameIndex = Math.floor(Date.now() / 100) % 2;
        sprite.texture = this._superSheepTextures[frameIndex];
      }
    }
  }

  updateKamikazeSprite(
    container: Container & { frameIndex?: number; textures?: Texture[]; frameTime?: number },
    kamikaze: { position: { x: number; y: number } },
    deltaTime: number,
  ): void {
    container.position.set(kamikaze.position.x, kamikaze.position.y);

    if (container.textures && container.textures.length > 0) {
      container.frameTime = (container.frameTime || 0) + deltaTime;
      if (container.frameTime >= 0.05) {
        container.frameTime = 0;
        container.frameIndex = ((container.frameIndex || 0) + 1) % container.textures.length;
        const sprite = container.getChildByLabel('kami') as Sprite;
        if (sprite) {
          sprite.texture = container.textures[container.frameIndex];
        }
      }
    }
  }

  updateCrateSprite(
    container: Container,
    crate: { position: { x: number; y: number }; parachuteOpen: boolean },
  ): void {
    const yOffset = crate.parachuteOpen ? 0 : 8;
    container.position.set(crate.position.x, crate.position.y + yOffset);

    const parachute = container.getChildByLabel('parachute');
    const box = container.getChildByLabel('box');
    if (parachute && box) {
      parachute.visible = crate.parachuteOpen;
      box.visible = !crate.parachuteOpen;
    }
  }

  updateBarrelSprite(
    sprite: Container | Sprite,
    barrel: { position: { x: number; y: number } },
  ): void {
    sprite.position.set(barrel.position.x, barrel.position.y + 12);
  }

  updateFireSprite(
    sprite: Graphics,
    fire: {
      position: { x: number; y: number };
      getFlickerIntensity(): number;
      getRemainingLife(): number;
    },
  ): void {
    sprite.position.set(fire.position.x, fire.position.y);
    sprite.clear();
    const intensity = fire.getFlickerIntensity();
    const life = fire.getRemainingLife();
    const baseSize = 8 + Math.sin(performance.now() * 0.01) * 2;

    sprite.ellipse(0, -baseSize * 0.3, baseSize * 0.6, baseSize * life);
    sprite.fill({ color: 0xff6600, alpha: 0.9 * intensity * life });

    sprite.ellipse(0, -baseSize * 0.5, baseSize * 0.4, baseSize * 0.7 * life);
    sprite.fill({ color: 0xffcc00, alpha: 0.8 * intensity * life });

    sprite.ellipse(0, -baseSize * 0.6, baseSize * 0.2, baseSize * 0.4 * life);
    sprite.fill({ color: 0xffffaa, alpha: 0.6 * intensity * life });
  }

  updateGraveSprite(
    sprite: Container | Sprite,
    grave: { position: { x: number; y: number } },
  ): void {
    sprite.position.set(grave.position.x, grave.position.y + 30);
  }

  updateNapalmDropletSprite(
    sprite: Graphics,
    droplet: { position: { x: number; y: number }; velocity: { x: number; y: number } },
  ): void {
    sprite.position.set(droplet.position.x, droplet.position.y);
    sprite.rotation = Math.atan2(droplet.velocity.y, droplet.velocity.x) - Math.PI / 2;
  }

  cleanupOrphanedSprites<T extends { destroy(options?: { children?: boolean }): void }>(
    spriteMap: Map<string, T>,
    existingIds: Set<string>,
    destroyChildren = false,
  ): void {
    for (const [id, sprite] of spriteMap) {
      if (!existingIds.has(id)) {
        sprite.destroy(destroyChildren ? { children: true } : undefined);
        spriteMap.delete(id);
      }
    }
  }
}
